import { supabase } from '../lib/supabase';


// Helper functions for case conversion
const toCamelCase = (str) => {
  return str?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
};

const toSnakeCase = (str) => {
  return str?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
};

const convertKeysToCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = convertKeysToCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

const convertKeysToSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = toSnakeCase(key);
      acc[snakeKey] = convertKeysToSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Wearable Device Connection Service
export const wearableConnectionService = {
  async getConnections(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('wearable_connections')
        ?.select('*')
        ?.eq('client_id', clientId)
        ?.order('created_at', { ascending: false });

      if (error) {
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async addConnection(connection) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const snakeCaseConnection = convertKeysToSnakeCase({ ...connection, clientId: user?.id });
      const { data, error } = await supabase
        ?.from('wearable_connections')
        ?.insert(snakeCaseConnection)
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateConnection(connectionId, updates) {
    try {
      const snakeCaseUpdates = convertKeysToSnakeCase(updates);
      const { data, error } = await supabase
        ?.from('wearable_connections')
        ?.update(snakeCaseUpdates)
        ?.eq('id', connectionId)
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteConnection(connectionId) {
    try {
      const { error } = await supabase
        ?.from('wearable_connections')
        ?.delete()
        ?.eq('id', connectionId);

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async testConnection(connectionId) {
    try {
      const { data: connection, error: fetchError } = await supabase
        ?.from('wearable_connections')
        ?.select('*')
        ?.eq('id', connectionId)
        ?.single();

      if (fetchError) {
        return { success: false, error: fetchError };
      }

      // Simulate API connection test
      const testResult = await this.simulateDeviceTest(connection?.device_type);
      
      return { success: testResult?.success, error: testResult?.error };
    } catch (error) {
      return { success: false, error };
    }
  },

  async simulateDeviceTest(deviceType) {
    // Simulate API test with random success/failure
    return new Promise((resolve) => {
      setTimeout(() => {
        const success = Math.random() > 0.2; // 80% success rate
        resolve({
          success,
          error: success ? null : { message: `Failed to connect to ${deviceType}. Please check your credentials.` }
        });
      }, 1500);
    });
  }
};

// Wearable Data Sync Service
export const wearableDataService = {
  async getWearableData(clientId, startDate, endDate, deviceType = null) {
    try {
      let query = supabase
        ?.from('wearable_data')
        ?.select('*')
        ?.eq('client_id', clientId);

      if (startDate) {
        query = query?.gte('recorded_at', startDate);
      }
      if (endDate) {
        query = query?.lte('recorded_at', endDate);
      }
      if (deviceType) {
        query = query?.eq('device_type', deviceType);
      }

      query = query?.order('recorded_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async syncDeviceData(connectionId) {
    try {
      const { data: connection, error: fetchError } = await supabase
        ?.from('wearable_connections')
        ?.select('*')
        ?.eq('id', connectionId)
        ?.single();

      if (fetchError) {
        return { data: null, error: fetchError };
      }

      // Simulate fetching data from device API
      const syncedData = await this.simulateDeviceSync(connection);

      // Update last sync timestamp
      await supabase
        ?.from('wearable_connections')
        ?.update({ last_sync_at: new Date()?.toISOString() })
        ?.eq('id', connectionId);

      return { data: syncedData, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async simulateDeviceSync(connection) {
    // Simulate API data fetch
    return new Promise((resolve) => {
      setTimeout(() => {
        const mockData = {
          heartRate: Math.floor(Math.random() * 40) + 60,
          steps: Math.floor(Math.random() * 5000) + 5000,
          calories: Math.floor(Math.random() * 1000) + 1500,
          sleepHours: Math.floor(Math.random() * 3) + 6,
          activeMinutes: Math.floor(Math.random() * 60) + 30,
          distance: (Math.random() * 5 + 2).toFixed(2)
        };
        resolve(mockData);
      }, 2000);
    });
  },

  async addWearableData(wearableData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const snakeCaseData = convertKeysToSnakeCase({ ...wearableData, clientId: user?.id });
      const { data, error } = await supabase
        ?.from('wearable_data')
        ?.insert(snakeCaseData)
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Field Mapping Service
export const fieldMappingService = {
  async getFieldMappings(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('wearable_field_mappings')
        ?.select('*')
        ?.eq('client_id', clientId);

      if (error) {
        return { data: [], error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async saveFieldMapping(mapping) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const snakeCaseMapping = convertKeysToSnakeCase({ ...mapping, clientId: user?.id });
      const { data, error } = await supabase
        ?.from('wearable_field_mappings')
        ?.upsert(snakeCaseMapping)
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteFieldMapping(mappingId) {
    try {
      const { error } = await supabase
        ?.from('wearable_field_mappings')
        ?.delete()
        ?.eq('id', mappingId);

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  }
};
